import { useCallback, useMemo } from 'react';
import { createEventBus } from '../../../../../emitter';
import type { WithRequired } from '../../../../../types';
import { useStoreRefs } from '../../../../hooks/useStoreRefs';
import type { DataSourceRecordStoreState } from '../../../../stores/DataSourceRecordStore';
import type { BuilderInterfaceStoreStateInitialValue } from '../../../../stores/InterfaceStore/builder';
import type { InterfaceEventBusType } from '../../../../types/eventBus';
import type { InterfaceAppProvidersProps } from '../InterfaceAppProviders';

function useInterfaceAppProvidersInitialState(
  params: WithRequired<InterfaceAppProvidersProps, 'mode'>,
) {
  const {
    interfaceRecord,
    dataSources,
    mode,
    globalInterfacePage,
    parentInterfaceRecord,
    interfaceResourceProviderRef,
    onInterfaceResourceProviderRefInit,
  } = params;
  const interfaceId = interfaceRecord.id;
  const eventBus = useMemo(() => createEventBus<InterfaceEventBusType>(interfaceId), [interfaceId]);
  const { dependencyGraphStoreRef, interfaceStoreRef, globalStateStoreRef } = useStoreRefs({
    interfaceResourceProviderRef,
    onInterfaceResourceProviderRefInit,
    eventBus,
  });
  const initialDataSourceRecordStoreState = useMemo<Partial<DataSourceRecordStoreState>>(() => {
    return {
      dataSources: dataSources ?? {},
    };
  }, [dataSources]);

  const getDependencyGraphActions = useCallback(() => {
    if (!dependencyGraphStoreRef.current) {
      throw new Error('AppDependency Graph Store Ref is not available');
    }
    return dependencyGraphStoreRef.current;
  }, [dependencyGraphStoreRef]);

  const interfaceStoreInitialValue: BuilderInterfaceStoreStateInitialValue = useMemo(() => {
    // none of these are used in the codebase, these are just to satisfy the interfaceStoreInitialValue type which is built to handle building single pages at a time
    return {
      mode,
      isInteractiveModeEnabled: false,
      activeBlockId: null,
      activeInterfacePageId: globalInterfacePage?.id ?? '',
      activeInterfacePage: globalInterfacePage,
      interfaceRecord,
      parentInterfaceRecord,
      interfacePages: {
        [globalInterfacePage?.id ?? '']: globalInterfacePage,
      },
      isPublic: false,
      interfacePageNamespaces: {},
      disableDevTools: false,
    };
  }, [mode, interfaceRecord, globalInterfacePage, parentInterfaceRecord]);

  return {
    globalStateStoreRef,
    dependencyGraphStoreRef,
    initialDataSourceRecordStoreState,
    interfaceStoreInitialValue,
    getDependencyGraphActions,
    interfaceStoreRef,
  };
}

export default useInterfaceAppProvidersInitialState;
